import { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  BookText, Loader2, Sparkles, ChevronRight, RefreshCw, ArrowLeft,
  Lightbulb, AlertTriangle, CheckCircle2, Search, FolderPlus, X, Send,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * QPR-Lexikon — KI-Nachschlagewerk für alle 26 QPR-Kriterien.
 * URL: /campus/app/qpr-lexikon
 *
 * Sidebar links: 7 Prüfbereiche mit den 26 Kriterien.
 * Rechts: Erklärung + Beispielformulierungen + Fallstricke + Praxis-Tipps.
 * Erste Öffnung eines Kriteriums generiert via Claude (Cache), danach sofort.
 */
export default function QprLexikon() {
  const [loading, setLoading] = useState(true);
  const [bereiche, setBereiche] = useState([]);
  const [activeNr, setActiveNr] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [search, setSearch] = useState("");
  const [insertTarget, setInsertTarget] = useState(null); // {text, kriterium}

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/qpr-lexikon`, { withCredentials: true });
      setBereiche(data.bereiche || []);
      // Erste Kriterium vorwählen
      const first = data.bereiche?.[0]?.kriterien?.[0];
      if (first) setActiveNr(first.nr);
    } catch (e) {
      toast.error("QPR-Lexikon konnte nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);

  const loadDetail = useCallback(async (nr) => {
    setDetailLoading(true);
    setDetail(null);
    try {
      const { data } = await axios.get(`${API}/qpr-lexikon/${nr}`, { withCredentials: true });
      setDetail(data);
    } catch (e) {
      toast.error("Kriterium konnte nicht geladen werden.");
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeNr) loadDetail(activeNr);
  }, [activeNr, loadDetail]);

  const generate = async (force = false) => {
    if (!activeNr) return;
    setGenerating(true);
    try {
      const { data } = await axios.post(
        `${API}/qpr-lexikon/${activeNr}/generate`,
        { force_refresh: force },
        { withCredentials: true },
      );
      setDetail({
        kriterium: data.kriterium,
        erklaerung: data.erklaerung,
        beispielformulierungen: data.beispielformulierungen,
        fallstricke: data.fallstricke,
        praxis_tipps: data.praxis_tipps,
        generated_at: data.generated_at,
      });
      if (data.cached) {
        toast.success("Bereits generiert · aus Cache geladen");
      } else {
        toast.success("KI-Erklärung erzeugt");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "KI-Antwort fehlgeschlagen.");
    } finally {
      setGenerating(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return bereiche;
    return bereiche
      .map((b) => ({
        ...b,
        kriterien: b.kriterien.filter(
          (k) => k.nr.toLowerCase().includes(q) || k.text.toLowerCase().includes(q)
        ),
      }))
      .filter((b) => b.kriterien.length > 0);
  }, [bereiche, search]);

  if (loading) {
    return (
      <div className="min-h-screen bg-cream-50 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-brand-darkred" />
      </div>
    );
  }

  return (
    <div data-testid="qpr-lexikon" className="min-h-screen bg-cream-50 pb-20">
      <div className="border-b border-cream-300 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <Link
            to="/campus/app"
            className="inline-flex items-center gap-1.5 text-[13px] text-sand-600 hover:text-brand-darkred mb-3"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Zurück zum Dashboard
          </Link>
          <div className="flex items-center gap-2 mb-1">
            <span className="inline-flex items-center rounded-full bg-brand-darkred text-cream-50 text-[10px] font-bold tracking-widest px-2 py-0.5">
              KI-NACHSCHLAGEWERK
            </span>
            <span className="text-[11px] text-sand-500">26 Kriterien · 7 Prüfbereiche</span>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl text-sand-800 leading-none flex items-center gap-2">
            <BookText className="h-7 w-7 text-brand-darkred" strokeWidth={1.6} />
            QPR-Lexikon
          </h1>
          <p className="text-[14px] text-sand-600 mt-2 max-w-2xl leading-relaxed">
            Für jedes QPR-2026-Kriterium: KI-Erklärung, fertige Formulierungen für die Doku,
            typische Prüf-Fallstricke und Praxis-Tipps. Erste Öffnung generiert via Claude
            Sonnet 4.6, danach sofort aus dem Cache.
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 grid grid-cols-1 md:grid-cols-[280px_1fr] gap-4 md:gap-6">
        {/* Sidebar Kriterien-Liste */}
        <aside className="rounded-2xl bg-white border border-cream-300 p-3 h-fit md:sticky md:top-6">
          <div className="relative mb-2">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-sand-400" />
            <input
              data-testid="qpr-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Suchen …"
              className="w-full rounded-lg border border-cream-300 bg-white pl-8 pr-2 py-1.5 text-[13px] text-sand-800 focus:outline-none focus:border-brand-darkred/60"
            />
          </div>
          <div className="max-h-[65vh] md:max-h-[70vh] overflow-y-auto space-y-3 pr-1">
            {filtered.map((b) => (
              <div key={b.id}>
                <div className="px-2 text-[10px] font-bold uppercase tracking-widest text-sand-500 mb-1">
                  {b.id} · {b.titel}
                </div>
                <ul className="space-y-0.5">
                  {b.kriterien.map((k) => {
                    const isActive = k.nr === activeNr;
                    return (
                      <li key={k.nr}>
                        <button
                          data-testid={`qpr-item-${k.nr}`}
                          onClick={() => setActiveNr(k.nr)}
                          className={`w-full text-left rounded-lg px-2.5 py-2 text-[12.5px] leading-snug flex gap-2 transition-colors ${
                            isActive
                              ? "bg-brand-darkred text-cream-50 font-semibold"
                              : "text-sand-700 hover:bg-cream-100"
                          }`}
                        >
                          <span className={`tabular-nums text-[11px] font-bold w-8 shrink-0 ${isActive ? "text-cream-200" : "text-brand-darkred"}`}>{k.nr}</span>
                          <span className="flex-1">{k.text}</span>
                          <ChevronRight className={`h-3.5 w-3.5 shrink-0 mt-0.5 ${isActive ? "" : "opacity-0"}`} />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </aside>

        {/* Content */}
        <section data-testid="qpr-detail" className="rounded-2xl bg-white border border-cream-300 p-5 sm:p-8 min-h-[400px]">
          {!activeNr && (
            <div className="text-center py-16 text-sand-600 text-[14px]">
              Wähle links ein Kriterium.
            </div>
          )}
          {activeNr && detailLoading && (
            <div className="flex items-center justify-center py-16 text-sand-600 text-[14px]">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Lade Kriterium …
            </div>
          )}
          {activeNr && !detailLoading && detail && (
            <>
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-sand-500 mb-2">
                <span className="text-brand-darkred font-bold">{detail.kriterium.nr}</span>
                <span>·</span>
                <span>Bereich {detail.kriterium.bereich_id} · {detail.kriterium.bereich_titel}</span>
              </div>
              <h2 className="font-display text-2xl sm:text-3xl text-sand-800 leading-tight mb-4">
                {detail.kriterium.text}
              </h2>

              {!detail.erklaerung && (
                <div className="rounded-xl bg-cream-100 border border-cream-300 p-5 text-center">
                  <p className="text-[14px] text-sand-700 mb-3">
                    Für dieses Kriterium liegt noch keine KI-Erklärung vor.
                  </p>
                  <button
                    data-testid="btn-qpr-generate"
                    onClick={() => generate(false)}
                    disabled={generating}
                    className="inline-flex items-center gap-2 rounded-full bg-brand-darkred text-cream-50 px-5 py-2.5 text-[13.5px] font-semibold hover:bg-brand-red disabled:opacity-60"
                  >
                    {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                    Mit KI erklären lassen
                  </button>
                  <p className="text-[11px] text-sand-500 mt-3">
                    Claude Sonnet 4.6 · Antwort wird für alle Nutzer:innen im Team gecached.
                  </p>
                </div>
              )}

              {detail.erklaerung && (
                <div className="space-y-6">
                  <div>
                    <div className="text-[11px] font-bold uppercase tracking-wider text-brand-darkred mb-1">Erklärung</div>
                    <p className="text-[15px] text-sand-800 leading-relaxed">{detail.erklaerung}</p>
                  </div>

                  {detail.beispielformulierungen?.length > 0 && (
                    <div>
                      <div className="text-[11px] font-bold uppercase tracking-wider text-emerald-800 mb-2 flex items-center gap-1.5">
                        <CheckCircle2 className="h-3.5 w-3.5" /> Beispielformulierungen für die Doku (Note A)
                      </div>
                      <ul className="space-y-2">
                        {detail.beispielformulierungen.map((s, i) => (
                          <li
                            key={i}
                            data-testid={`qpr-beispiel-${i}`}
                            className="group rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-[13.5px] text-sand-800 leading-relaxed flex gap-2 items-start"
                          >
                            <span className="flex-1">„{s}“</span>
                            <button
                              onClick={() => setInsertTarget({ text: s, kriterium: detail.kriterium })}
                              data-testid={`btn-insert-mappe-${i}`}
                              title="In eine Patientenmappe einfügen"
                              className="shrink-0 inline-flex items-center gap-1 rounded-full bg-white border border-emerald-300 text-emerald-800 px-2.5 py-1 text-[11px] font-semibold hover:bg-emerald-100 opacity-70 group-hover:opacity-100 transition-opacity"
                            >
                              <FolderPlus className="h-3 w-3" /> In Mappe
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {detail.fallstricke?.length > 0 && (
                    <div>
                      <div className="text-[11px] font-bold uppercase tracking-wider text-red-800 mb-2 flex items-center gap-1.5">
                        <AlertTriangle className="h-3.5 w-3.5" /> Typische Fallstricke (führt zu B/C/D)
                      </div>
                      <ul className="space-y-1.5">
                        {detail.fallstricke.map((s, i) => (
                          <li key={i} className="text-[13.5px] text-sand-800 leading-relaxed pl-4 relative">
                            <span className="absolute left-0 top-2 h-1.5 w-1.5 rounded-full bg-red-500" />
                            {s}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {detail.praxis_tipps?.length > 0 && (
                    <div>
                      <div className="text-[11px] font-bold uppercase tracking-wider text-saffron-dark mb-2 flex items-center gap-1.5">
                        <Lightbulb className="h-3.5 w-3.5" /> Praxis-Tipps für morgen
                      </div>
                      <ul className="space-y-1.5">
                        {detail.praxis_tipps.map((s, i) => (
                          <li key={i} className="text-[13.5px] text-sand-800 leading-relaxed pl-4 relative">
                            <span className="absolute left-0 top-2 h-1.5 w-1.5 rounded-full bg-saffron" />
                            {s}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="flex items-center justify-between text-[11.5px] text-sand-500 pt-3 border-t border-cream-200">
                    <span>Generiert {detail.generated_at ? new Date(detail.generated_at).toLocaleDateString("de-DE") : "—"}</span>
                    <button
                      data-testid="btn-qpr-regenerate"
                      onClick={() => generate(true)}
                      disabled={generating}
                      className="inline-flex items-center gap-1 text-sand-600 hover:text-brand-darkred disabled:opacity-60"
                    >
                      {generating ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                      Neu generieren
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </section>
      </div>

      {insertTarget && (
        <InsertToMappeModal
          text={insertTarget.text}
          kriterium={insertTarget.kriterium}
          onClose={() => setInsertTarget(null)}
        />
      )}
    </div>
  );
}

/* -------------------------------------------------------
 * InsertToMappeModal · fügt eine QPR-Beispielformulierung
 * als 360°-Beobachtungseintrag in eine gewählte Mappe ein.
 * ------------------------------------------------------- */
function InsertToMappeModal({ text, kriterium, onClose }) {
  const [mappen, setMappen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState("");
  const [customText, setCustomText] = useState(text);
  const [ampel, setAmpel] = useState("gruen");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/patientenmappe`, { withCredentials: true });
        setMappen(data.items || []);
        if (data.items?.length === 1) setSelectedId(data.items[0].id);
      } catch {
        toast.error("Mappen konnten nicht geladen werden.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const submit = async () => {
    if (!selectedId) {
      toast.error("Bitte eine Mappe wählen.");
      return;
    }
    if (!customText.trim()) {
      toast.error("Text darf nicht leer sein.");
      return;
    }
    setSubmitting(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      await axios.post(
        `${API}/patientenmappe/${selectedId}/section/beobachtung_360/entry`,
        {
          datum: today,
          autor: "QPR-Lexikon",
          kategorie: `QPR · ${kriterium.nr}`,
          ampel,
          text: customText.trim(),
        },
        { withCredentials: true },
      );
      const target = mappen.find((m) => m.id === selectedId);
      toast.success(`In „${target?.label || "Mappe"}" eingefügt`);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Einfügen fehlgeschlagen.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="insert-mappe-modal"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-espresso-deep/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-hover max-w-lg w-full max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-cream-300">
          <h3 className="font-display text-xl text-sand-800">
            In Patientenmappe übernehmen
          </h3>
          <button
            onClick={onClose}
            className="p-1 rounded-full text-sand-400 hover:text-sand-800"
            aria-label="Schließen"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div className="text-[11px] text-sand-500 uppercase tracking-wider font-semibold">
            Aus QPR-Kriterium {kriterium.nr}
          </div>

          {loading && (
            <div className="text-center py-4 text-sand-600 text-[13px]">
              <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Lade Mappen …
            </div>
          )}

          {!loading && mappen.length === 0 && (
            <div className="rounded-xl bg-cream-100 border border-cream-300 p-4 text-[13px] text-sand-700 text-center">
              Du hast noch keine Patientenmappen. Lege zuerst eine an.
            </div>
          )}

          {!loading && mappen.length > 0 && (
            <>
              <label className="block">
                <span className="block text-[11.5px] font-semibold uppercase tracking-wider text-sand-600 mb-1">
                  Ziel-Mappe *
                </span>
                <select
                  data-testid="insert-select-mappe"
                  value={selectedId}
                  onChange={(e) => setSelectedId(e.target.value)}
                  className="w-full rounded-lg border border-cream-300 bg-white px-3 py-2 text-[14px] text-sand-800 focus:outline-none focus:border-brand-darkred/60"
                >
                  <option value="">— Bitte wählen —</option>
                  {mappen.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.label}
                      {m.risk_level && m.risk_level !== "gruen" ? ` · Ampel ${m.risk_level}` : ""}
                    </option>
                  ))}
                </select>
              </label>

              <label className="block">
                <span className="block text-[11.5px] font-semibold uppercase tracking-wider text-sand-600 mb-1">
                  Text (editierbar)
                </span>
                <textarea
                  data-testid="insert-textarea"
                  value={customText}
                  onChange={(e) => setCustomText(e.target.value)}
                  rows={5}
                  className="w-full rounded-lg border border-cream-300 bg-white px-3 py-2 text-[13.5px] text-sand-800 focus:outline-none focus:border-brand-darkred/60 leading-relaxed"
                />
              </label>

              <label className="block">
                <span className="block text-[11.5px] font-semibold uppercase tracking-wider text-sand-600 mb-1">
                  Ampel
                </span>
                <div className="flex gap-2">
                  {[
                    { v: "gruen", label: "grün", cls: "bg-emerald-500" },
                    { v: "gelb", label: "gelb", cls: "bg-amber-400" },
                    { v: "rot", label: "rot", cls: "bg-red-600" },
                  ].map((o) => (
                    <button
                      key={o.v}
                      type="button"
                      onClick={() => setAmpel(o.v)}
                      className={`flex-1 rounded-lg border px-3 py-2 text-[12.5px] font-semibold inline-flex items-center justify-center gap-1.5 ${
                        ampel === o.v
                          ? "border-brand-darkred bg-cream-100 text-brand-darkred"
                          : "border-cream-300 bg-white text-sand-700 hover:border-brand-darkred/40"
                      }`}
                    >
                      <span className={`inline-block h-2 w-2 rounded-full ${o.cls}`} />
                      {o.label}
                    </button>
                  ))}
                </div>
              </label>

              <div className="rounded-xl bg-cream-50 border border-cream-200 px-3 py-2 text-[11.5px] text-sand-600 leading-relaxed">
                Der Eintrag wird als <strong>360°-Beobachtung</strong> in die Mappe geschrieben,
                mit Kategorie <code className="text-brand-darkred">QPR · {kriterium.nr}</code>.
              </div>

              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 rounded-full border border-cream-300 py-2.5 text-[13.5px] font-semibold text-sand-700"
                >
                  Abbrechen
                </button>
                <button
                  type="button"
                  data-testid="btn-insert-submit"
                  onClick={submit}
                  disabled={submitting || !selectedId}
                  className="flex-1 rounded-full bg-brand-darkred text-cream-50 py-2.5 text-[13.5px] font-semibold hover:bg-brand-red disabled:opacity-60 inline-flex items-center justify-center gap-2"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  In Mappe einfügen
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
